// Thin fetch wrapper for the site's own serverless API. Public endpoints
// need no auth; admin endpoints take a token the caller supplies (kept in
// sessionStorage by the admin page, never in localStorage).

async function request(path, { method = 'GET', body, token, headers } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { 'x-admin-token': token } : {}),
      ...headers
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const data = isJson ? await res.json().catch(() => ({})) : null;

  if (!res.ok) {
    const error = new Error((data && (data.error || (data.errors && JSON.stringify(data.errors)))) || `Request failed (${res.status})`);
    error.status = res.status;
    error.data = data;
    throw error;
  }
  return data;
}

export const api = {
  listVideos: () => request('/api/videos/list'),
  listArticles: () => request('/api/articles/list'),
  getArticle: (slug) => request(`/api/articles/${encodeURIComponent(slug)}`),
  submitContact: (payload) => request('/api/contact', { method: 'POST', body: payload })
};

export const adminApi = {
  listVideos: (token) => request('/api/admin/videos', { token }),
  createVideo: (token, body) => request('/api/admin/videos', { method: 'POST', token, body }),
  updateVideo: (token, id, body) => request(`/api/admin/videos/${id}`, { method: 'PUT', token, body }),
  deleteVideo: (token, id) => request(`/api/admin/videos/${id}`, { method: 'DELETE', token }),

  listArticles: (token) => request('/api/admin/articles', { token }),
  createArticle: (token, body) => request('/api/admin/articles', { method: 'POST', token, body }),
  updateArticle: (token, id, body) => request(`/api/admin/articles/${id}`, { method: 'PUT', token, body }),
  deleteArticle: (token, id) => request(`/api/admin/articles/${id}`, { method: 'DELETE', token }),

  listSubmissions: (token) => request('/api/admin/submissions', { token }),

  uploadImage: async (token, file) => {
    const res = await fetch(`/api/admin/image-upload?filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers: { 'Content-Type': file.type, 'x-admin-token': token },
      body: file
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data.error || `Upload failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return data;
  }
};

const ADMIN_TOKEN_KEY = 'alamen.admin.token';

export function getStoredAdminToken() {
  try {
    return window.sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setStoredAdminToken(token) {
  try {
    if (token) window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
